import React from 'react';

// Renders the KR visibility-preview payload. Clearly indicates whether the
// preview reflects the saved (persisted) event or unsaved proposed changes.
export default function VisibilityPreviewPanel({ preview, mode = 'saved', loading = false }) {
  if (loading) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs" data-testid="visibility-preview-loading">
        Evaluating visibility…
      </div>
    );
  }
  if (!preview) return null;

  const modeLabel = mode === 'proposed' ? 'PROPOSED (unsaved changes)' : 'SAVED';
  const modeTone = mode === 'proposed'
    ? 'bg-amber-50 border-amber-200 text-amber-800'
    : 'bg-slate-100 border-slate-200 text-slate-700';

  const blockers = preview.publication_blockers || [];
  const warnings = preview.warnings || [];

  return (
    <div
      className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2 text-xs"
      data-testid="visibility-preview-panel"
      data-preview-mode={mode}
    >
      <div className="flex items-center justify-between">
        <div className="font-semibold text-slate-800">Visibility preview</div>
        <span
          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${modeTone}`}
          data-testid="visibility-preview-mode-badge"
        >
          {modeLabel}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
        <div><span className="text-slate-500">Scope:</span>{' '}
          <span className="font-medium text-slate-800">{preview.effective_visibility?.scope}</span>
        </div>
        {preview.effective_visibility?.segment && (
          <div><span className="text-slate-500">Audience:</span> {preview.effective_visibility.segment}</div>
        )}
        {preview.estimated_visible_users != null && (
          <div data-testid="visibility-preview-estimated">
            <span className="text-slate-500">Est. visible users:</span> {preview.estimated_visible_users}
          </div>
        )}
        <div><span className="text-slate-500">Publication ready:</span>{' '}
          <span
            className={preview.publication_ready ? 'text-emerald-700 font-medium' : 'text-rose-700 font-medium'}
            data-testid="visibility-preview-ready"
          >
            {preview.publication_ready ? 'Yes' : 'No'}
          </span>
        </div>
      </div>

      {blockers.length > 0 && (
        <div
          className="rounded border border-rose-200 bg-rose-50 p-2 text-rose-800"
          data-testid="visibility-preview-blockers"
        >
          <div className="font-semibold mb-1">Blocking errors</div>
          <ul className="list-disc ml-4 space-y-0.5">
            {blockers.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        </div>
      )}

      {warnings.length > 0 && (
        <div
          className="rounded border border-amber-200 bg-amber-50 p-2 text-amber-800"
          data-testid="visibility-preview-warnings"
        >
          <div className="font-semibold mb-1">Warnings (non-blocking)</div>
          <ul className="list-disc ml-4 space-y-0.5">
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
